// Mock authentication service for local development

import { getEnvBoolean } from '@/utils/env'
import type { SignInInput, SignInOutput } from 'aws-amplify/auth'

interface MockUser {
  username: string
  signInDetails: {
    loginId: string
  }
  userId: string
  attributes: {
    email: string
    email_verified: boolean
    'custom:user_role'?: string
  }
}

type AuthListener = (user: MockUser | null, isAuthenticated: boolean) => void

class MockAuthService {
  private currentUser: MockUser | null
  private isAuthenticated: boolean
  private listeners: Set<AuthListener>

  constructor() {
    this.currentUser = null
    this.isAuthenticated = false
    this.listeners = new Set()
  }

  // Mock user data
  getMockUser(): MockUser {
    return {
      username: 'mockuser',
      signInDetails: {
        loginId: 'mockuser@example.com',
      },
      userId: 'mock-user-id',
      attributes: {
        email: 'mockuser@example.com',
        email_verified: true,
        'custom:user_role': 'root',
      },
    }
  }

  // Mock sign in
  async signIn(parameters: SignInInput): Promise<SignInOutput> {
    console.log('Mock Auth: Signing in user', parameters.username)

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500))

    // Accept any credentials for development
    this.currentUser = this.getMockUser()
    this.isAuthenticated = true

    // Notify listeners
    this.notifyListeners()

    return { isSignedIn: true, nextStep: { signInStep: 'DONE' } }
  }

  // Mock sign out
  async signOut() {
    console.log('Mock Auth: Signing out user')

    this.currentUser = null
    this.isAuthenticated = false

    // Notify listeners
    this.notifyListeners()
  }

  // Mock get current user
  async getCurrentUser() {
    if (this.isAuthenticated && this.currentUser) {
      return this.currentUser
    }

    // Check if user was previously authenticated (simulate session storage)
    const storedAuth = localStorage.getItem('mockAuth')
    if (storedAuth === 'true') {
      this.currentUser = this.getMockUser()
      this.isAuthenticated = true
      return this.currentUser
    }

    throw new Error('No authenticated user')
  }

  // Mock sign up
  async signUp({ username, password, email }: { username: string; password: string; email?: string }) {
    console.log('Mock Auth: Signing up user', username)

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500))

    return {
      userId: 'mock-user-id',
      username,
      isSignUpComplete: true,
    }
  }

  // Mock confirm sign up
  async confirmSignUp({ username, confirmationCode }: { username: string; confirmationCode: string }) {
    console.log('Mock Auth: Confirming sign up', username)

    // Accept any confirmation code
    await new Promise((resolve) => setTimeout(resolve, 500))

    return {
      isSignUpComplete: true,
    }
  }

  // Mock forgot password
  async forgotPassword(username: string) {
    console.log('Mock Auth: Forgot password for', username)

    await new Promise((resolve) => setTimeout(resolve, 500))

    return {
      CodeDeliveryDetails: {
        DeliveryMedium: 'EMAIL',
        Destination: 'm***@e***.com',
      },
    }
  }

  // Mock confirm forgot password
  async confirmForgotPassword({ username, confirmationCode, newPassword }: { username: string; confirmationCode: string; newPassword: string }) {
    console.log('Mock Auth: Confirming forgot password for', username)

    await new Promise((resolve) => setTimeout(resolve, 500))

    return {
      success: true,
    }
  }

  // Add listener for auth state changes
  addListener(callback: AuthListener): () => void {
    this.listeners.add(callback)

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  // Notify all listeners of auth state changes
  notifyListeners(): void {
    this.listeners.forEach((callback) => {
      try {
        callback(this.currentUser, this.isAuthenticated)
      } catch (error) {
        console.error('Error in auth listener:', error)
      }
    })
  }

  // Simulate persistent session
  persistSession() {
    if (this.isAuthenticated) {
      localStorage.setItem('mockAuth', 'true')
    } else {
      localStorage.removeItem('mockAuth')
    }
  }

  // Auto-login for development convenience
  async autoLogin() {
    if (getEnvBoolean('VITE_AUTO_LOGIN')) {
      console.log('Mock Auth: Auto-login enabled')
      await this.signIn({ username: 'mockuser@example.com', password: 'password123' })
      this.persistSession()
    }
  }
}

// Export singleton instance
const mockAuthService = new MockAuthService()
export default mockAuthService
